import React, { useState } from "react";
import {
  Box,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  Card,
  CardContent,
  CircularProgress,
  Alert,
} from "@mui/material";

// Add a type for the PEB result rows
interface PebRow {
  nombres?: string;
  appat?: string;
  apmat?: string;
  [key: string]: any;
}

const columns = [
  "ciudadano",
  "nombres",
  "appat",
  "apmat",
  "apesp",
  "fechanac",
  "tipocedula",
  "numerodoc",
  "mesa",
  "mesa1",
  "estadoregistro",
  "estado",
  "dom1",
  "dom2",
  "sexo",
  "estadocivil",
  "fechains",
  "tiporegistro",
  "idlocnac",
  "fechaestado",
  "id_person",
  "numerotramite",
  "dep",
  "nomdep",
  "prov",
  "nomprov",
  "sec",
  "nomsec",
  "idloc",
  "nomloc",
  "dist",
  "nomdist",
  "zona",
  "nomzona",
  "reci",
  "nomreci",
  "cinote",
  "nomnote",
  "idmesa",
  "inscritoshabilitados",
];

const allColumns = [
  "ciudadano",
  "nombres",
  "appat",
  "apmat",
  "dom2",
  "estado",
  "estadocivil",
  "estadoregistro",
  "fechaestado",
  "fechains",
  "fechanac",
  "id_person",
  "idloc",
  "idlocnac",
  "idmesa",
  "inscritoshabilitados",
  "mesa",
  "mesa1",
  "nomdep",
  "nomdist",
  "nomloc",
  "nomnote",
  "nomprov",
  "nomreci",
  "nomsec",
  "nomzona",
  "numerodoc",
  "numerotramite",
  "prov",
  "reci",
  "sec",
  "sexo",
  "tipocedula",
  "tiporegistro",
  "zona",
];

export default function PebSearch() {
  const [idPerson, setIdPerson] = useState("");
  const [nombres, setNombres] = useState("");
  const [appat, setAppat] = useState("");
  const [apmat, setApmat] = useState("");
  const [nombresPartial, setNombresPartial] = useState(false);
  const [appatPartial, setAppatPartial] = useState(false);
  const [apmatPartial, setApmatPartial] = useState(false);
  const [results, setResults] = useState<PebRow[]>([]);
  const [displayColumns, setDisplayColumns] = useState<string[]>([
    "ciudadano",
    "nombres",
    "appat",
    "apmat",
    "dom1",
  ]);
  const [logic, setLogic] = useState("AND");
  const [selectedRow, setSelectedRow] = useState<any | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [detailsError, setDetailsError] = useState<string | null>(null);
  const [numerodoc, setNumerodoc] = useState("");

  const handleSearch = async () => {
    setSelectedRow(null);
    setDetailsError(null);
    setLoadingDetails(false);
    setResults([]);
    if (numerodoc) {
      setLoadingDetails(true);
      try {
        const res = await fetch(`/api/peb/search/numerodoc/${numerodoc}`);
        const data = await res.json();
        if (data.error) {
          setDetailsError(data.error);
        } else {
          setSelectedRow(data);
        }
      } catch (err) {
        setDetailsError("Failed to fetch record by numerodoc.");
      } finally {
        setLoadingDetails(false);
      }
      return;
    }
    if (idPerson) {
      setLoadingDetails(true);
      try {
        const res = await fetch(`/api/peb/search/id_person/${idPerson}`);
        const data = await res.json();
        if (data.error) {
          setDetailsError(data.error);
        } else {
          setSelectedRow(data);
        }
      } catch (err) {
        setDetailsError("Failed to fetch record by id_person.");
      } finally {
        setLoadingDetails(false);
      }
      return;
    }
    const params = new URLSearchParams();
    if (nombres) params.append("nombres", nombres);
    if (appat) params.append("appat", appat);
    if (apmat) params.append("apmat", apmat);
    params.append("nombres_partial", nombresPartial ? "true" : "false");
    params.append("appat_partial", appatPartial ? "true" : "false");
    params.append("apmat_partial", apmatPartial ? "true" : "false");
    params.append("logic", logic);
    const res = await fetch(`/api/peb/search/advanced?${params.toString()}`);
    const data = await res.json();
    setResults(data);
  };

  const handleRowClick = async (row: any) => {
    setSelectedRow(null);
    setDetailsError(null);
    setLoadingDetails(true);
    try {
      const res = await fetch(`/api/peb/search/ciudadano/${row.ciudadano}`);
      const data = await res.json();
      if (data.error) {
        setDetailsError(data.error);
        setSelectedRow(null);
      } else {
        setSelectedRow(data);
      }
    } catch (err) {
      setDetailsError("Failed to fetch full record details.");
      setSelectedRow(null);
    } finally {
      setLoadingDetails(false);
    }
  };

  return (
    <Box sx={{ my: 4 }}>
      <Typography variant="h2" color="red">
        DEBUG: THIS IS THE REAL FILE
      </Typography>
      <Typography variant="h5" gutterBottom>
        PEB Search
      </Typography>
      <Box sx={{ display: "flex", gap: 2, mb: 2, alignItems: "center" }}>
        <TextField
          label="Document Number"
          value={numerodoc}
          onChange={(e) => setNumerodoc(e.target.value)}
          type="text"
        />
        <TextField
          label="ID Person"
          value={idPerson}
          onChange={(e) => setIdPerson(e.target.value)}
          type="number"
          disabled={!!numerodoc}
        />
        <TextField
          label="First Name"
          value={nombres}
          onChange={(e) => setNombres(e.target.value)}
          disabled={!!idPerson || !!numerodoc}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={nombresPartial}
              onChange={(e) => setNombresPartial(e.target.checked)}
              disabled={!!idPerson || !!numerodoc}
            />
          }
          label="Partial"
        />
        <TextField
          label="Last Name 1"
          value={appat}
          onChange={(e) => setAppat(e.target.value)}
          disabled={!!idPerson || !!numerodoc}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={appatPartial}
              onChange={(e) => setAppatPartial(e.target.checked)}
              disabled={!!idPerson || !!numerodoc}
            />
          }
          label="Partial"
        />
        <TextField
          label="Last Name 2"
          value={apmat}
          onChange={(e) => setApmat(e.target.value)}
          disabled={!!idPerson || !!numerodoc}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={apmatPartial}
              onChange={(e) => setApmatPartial(e.target.checked)}
              disabled={!!idPerson || !!numerodoc}
            />
          }
          label="Partial"
        />
        <Button variant="contained" onClick={handleSearch}>
          Search
        </Button>
      </Box>
      <Box sx={{ mb: 2 }}>
        <FormControl component="fieldset" sx={{ mr: 2 }}>
          <FormLabel component="legend">Search logic</FormLabel>
          <RadioGroup
            row
            value={logic}
            onChange={(e) => setLogic(e.target.value)}
            name="logic"
          >
            <FormControlLabel
              value="AND"
              control={<Radio />}
              label="AND (all fields must match)"
            />
            <FormControlLabel
              value="OR"
              control={<Radio />}
              label="OR (any field matches)"
            />
          </RadioGroup>
        </FormControl>
      </Box>
      <Box sx={{ mb: 2 }}>
        <TextField
          select
          SelectProps={{
            multiple: true,
            native: false,
            renderValue: (selected: any) => selected.join(", "),
          }}
          label="Columns to display"
          value={displayColumns}
          onChange={(e) => {
            const value = Array.isArray(e.target.value)
              ? e.target.value
              : [e.target.value];
            setDisplayColumns(value as string[]);
          }}
          fullWidth
        >
          {columns.map((col) => (
            <option key={col} value={col}>
              {col}
            </option>
          ))}
        </TextField>
      </Box>
      {loadingDetails && (
        <Box sx={{ mt: 3, mb: 3 }}>
          <CircularProgress />
        </Box>
      )}
      {detailsError && (
        <Box sx={{ mt: 3, mb: 3 }}>
          <Alert severity="error">{detailsError}</Alert>
        </Box>
      )}
      {selectedRow && !loadingDetails && !detailsError && (
        <Box
          sx={{
            mt: 3,
            mb: 3,
            maxWidth: 600,
            background: "#e3f2fd",
            p: 2,
            borderRadius: 2,
          }}
        >
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Full Record Details
              </Typography>
              {allColumns.map((key) => (
                <Box key={key} sx={{ display: "flex", mb: 1 }}>
                  <Typography sx={{ fontWeight: "bold", minWidth: 140 }}>
                    {key}:
                  </Typography>
                  <Typography sx={{ ml: 1 }}>
                    {selectedRow[key] !== null &&
                    selectedRow[key] !== undefined &&
                    selectedRow[key] !== "" ? (
                      String(selectedRow[key])
                    ) : (
                      <span style={{ color: "#aaa" }}>—</span>
                    )}
                  </Typography>
                </Box>
              ))}
            </CardContent>
          </Card>
        </Box>
      )}
      {results.length > 0 && (
        <Box sx={{ maxHeight: 600, overflow: "auto" }}>
          <Table>
            <TableHead>
              <TableRow>
                {columns.map((col) => (
                  <TableCell key={col}>{col}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {results.map((row, i) => (
                <TableRow
                  key={i}
                  hover
                  style={{ cursor: "pointer" }}
                  onClick={() => handleRowClick(row)}
                >
                  {columns.map((col) => (
                    <TableCell key={col}>{row[col]}</TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
      {results.length === 0 && (
        <Typography sx={{ mt: 2 }}>No records found.</Typography>
      )}
    </Box>
  );
}
